import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import * as LucideIcons from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { SERVICES_DATA, ServiceData } from "@/lib/services-data";

export interface CMSServiceRow {
  id: string;
  title: string;
  slug: string;
  short_description: string | null;
  full_description: string | null;
  icon_name: string;
  category: string;
  features: string[] | null;
  starting_price: string | null;
  show_starting_price: boolean;
  has_own_page: boolean;

  image_url: string | null;
  display_order: number;
  is_active: boolean;
}

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?q=80&w=2071&auto=format&fit=crop";

export const getIconByName = (name?: string | null): LucideIcon => {
  if (!name) return LucideIcons.Camera;
  const icons = LucideIcons as unknown as Record<string, LucideIcon>;
  return icons[name] || LucideIcons.Camera;
};

/**
 * Single source of truth for which services are live on the public site.
 *
 * The CMS (`cms_services`) decides which services exist: adding a service there
 * makes it appear everywhere (services page, home grid, footer, related
 * services); deactivating/deleting it removes it everywhere.
 * The static SERVICES_DATA is only used to enrich matching slugs (icons,
 * imagery, copy) and as a fallback while the CMS table is still empty.
 */
export function useActiveServices() {
  const { data: cmsServices = [], isLoading } = useQuery({
    queryKey: ["cms-services-active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cms_services")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });
      if (error) throw error;
      return (data || []) as CMSServiceRow[];
    },
    staleTime: 30_000,
  });

  const services = useMemo<ServiceData[]>(() => {
    // Nothing published yet — keep the site populated with the built-in set.
    if (cmsServices.length === 0) return SERVICES_DATA;

    return cmsServices.map((cms) => {
      const staticService = SERVICES_DATA.find((s) => s.slug === cms.slug);
      return {
        icon: cms.icon_name ? getIconByName(cms.icon_name) : staticService?.icon ?? LucideIcons.Camera,
        title: cms.title || staticService?.title || cms.slug,
        slug: cms.slug,
        description:
          cms.full_description || cms.short_description || staticService?.description || "",
        shortDescription:
          cms.short_description || staticService?.shortDescription || cms.full_description || "",
        features: cms.features?.length ? cms.features : staticService?.features ?? [],
        image: cms.image_url || staticService?.image || FALLBACK_IMAGE,
        category:
          (cms.category as ServiceData["category"]) || staticService?.category || "personal",
        href: cms.has_own_page === false ? "/contact" : `/services/${cms.slug}`,
      };
    });
  }, [cmsServices]);

  const cmsServiceMap = useMemo(
    () => new Map(cmsServices.map((s) => [s.slug, s])),
    [cmsServices]
  );

  return { services, cmsServices, cmsServiceMap, isLoading };
}
